import HubLeadCTA from "@/components/HubLeadCTA";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { VISA_PRODUCTS, VISA_PRODUCT_CATEGORIES, VISA_CATALOG_PRICE_MIN, VISA_CATALOG_PRICE_MAX } from "@/data/visa-products";
import { BRAND_URL } from "@/lib/brand";
import { ProductCatalogHubNav } from "@/components/ProductCatalogHubNav";

const TITLE = `Thailand Visa Application Services — ${VISA_PRODUCTS.length} Visa Types | NYC Legal`;
const DESC = `Full catalog of ${VISA_PRODUCTS.length} Thai visa services — Non-B, LTR 10-year, Smart, DTV, Elite, Retirement O-A/O-X, Marriage, ED, Tourist. Professional · free eligibility review. Free consultation, fast reply.`;

export const Route = createFileRoute("/en/visa/products/")({
  head: () => {
    const url = `${BRAND_URL}/en/visa/products`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/en/visa/products/index", "en")}, { name: "description", content: ctaDesc(DESC, "/en/visa/products/index", "en")},
        { property: "og:title", content: ctaTitle(TITLE, "/en/visa/products/index", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/visa/products/index", "en")},
        { property: "og:url", content: url }, { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "en", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: `${BRAND_URL}/visa/products` },
        { rel: "alternate", hrefLang: "x-default", href: `${BRAND_URL}/visa/products` },
      ],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [{
            "@type": "CollectionPage", "@id": `${url}#collection`,
            url, name: TITLE, inLanguage: "en",
            numberOfItems: VISA_PRODUCTS.length,
          }, {
            "@type": "ItemList", "@id": `${url}#itemlist`,
            itemListElement: VISA_PRODUCTS.map((p, i) => ({
              "@type": "ListItem", position: i + 1,
              url: `${BRAND_URL}/en/visa/products/${p.slug}`, name: p.nameEn,
            })),
          }, {
            "@type": "OfferCatalog", "@id": `${url}#catalog`, name: TITLE,
            itemListElement: VISA_PRODUCTS.map((p) => ({
              "@type": "Offer", name: p.nameEn, sku: p.sku,
              priceCurrency: "THB", price: p.priceMid,
              url: `${BRAND_URL}/en/visa/products/${p.slug}`,
            })),
          }],
        }),
      }],
    };
  },
  component: CatalogEN,
});

function CatalogEN() {
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-sky-100/60 to-background px-4 pt-16 pb-10">
        <div className="mx-auto max-w-6xl">
          <span className="inline-block rounded-full bg-sky-500/15 px-3 py-1 text-xs font-bold text-sky-700">Visa Product Catalog</span>
          <h1 className="mt-3 font-display text-3xl font-extrabold text-navy-deep sm:text-5xl">Thailand visa applications — {VISA_PRODUCTS.length} types</h1>
          <p data-speakable className="direct-answer mt-4 max-w-3xl rounded-xl border-l-4 border-sky-400 bg-sky-50/50 p-4 text-[15px] leading-relaxed text-slate-700">
            Professional fee THB {VISA_CATALOG_PRICE_MIN.toLocaleString()}–{VISA_CATALOG_PRICE_MAX.toLocaleString()} · free eligibility review within 24 hours · BOI / Immigration / Consular Affairs advisors — end-to-end from documents to approval follow-up.
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-4 py-10">
        {VISA_PRODUCT_CATEGORIES.map((c) => (
          <div key={c.id} className="mb-10">
            <h2 className="mb-4 font-display text-xl font-extrabold text-navy-deep">
              {c.labelEn} <span className="text-sm font-normal text-slate-500">({c.count} types)</span>
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {VISA_PRODUCTS.filter((p) => p.category === c.id).map((p) => (
                <Link key={p.sku} to="/en/visa/products/$slug" params={{ slug: p.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-sky-400 hover:shadow-md">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">{p.visa.code}</div>
                  <div className="mt-1 font-display font-extrabold text-navy-deep group-hover:text-sky-700">{p.nameEn}</div>
                  <div className="mt-2 text-xs text-slate-600 line-clamp-2">{p.shortDescEn}</div>
                  <div className="mt-3 text-xs font-semibold text-emerald-700">THB {p.priceMin.toLocaleString()}–{p.priceMax.toLocaleString()} · {p.turnaround}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
      <ProductCatalogHubNav locale="en" current="visa" />
    <HubLeadCTA locale="en" service="Visa Service" />
      </main>
  );
}
